/* cspell:ignore nics */
import { Base64 } from 'js-base64';
import { cloneDeep, get } from 'lodash';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { initCommandApiToFormFields } from '@/delivery-type-plugin/capabilities/command-args-normal';
import { healthCheckFromLivenessProbe, parseHealthCheckFromDeployConfig } from '@/delivery-type-plugin/capabilities/health-check';
import { readGroupSystemRegistry } from '@/delivery-type-plugin/capabilities/image';
import {
  IMAGE_STORAGE_VOLUMES_FIELD,
  rebuildImageStorageVolumes,
  serializeImageStorageVolumesField,
} from '@/delivery-type-plugin/capabilities/maas-image-storage';
import { normalizeMaasRuntime } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { MaasFramework, MaasModelSource, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MAAS_INFERENCE_SOURCE_FIELD, MaasRunMode as RunModeEnum } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  readMaasFormAnnotationSnapshot,
  readModelInstanceContainerDeployConfigFromWorkload,
} from '@/delivery-type-plugin/capabilities/maas-init/utils/annotations';
import { getFrameworkFromWorkload } from '@/delivery-type-plugin/capabilities/maas-init/utils/display';
import { parseFormJsonField, toFormJsonString } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  MODEL_CATEGORY_FORM_FIELD,
  getModelCategoryFromWorkload,
  hasModelCategory,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelCategory';
import type {
  MaasInferenceTopologySpec,
  MaasModel,
  MaasWorkload,
  ModelInstanceFormValues,
  ModelInstanceSpec,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { clearExistingPvcModelSourceForForm } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelSourcePlaceholder';
import { extractSourceRecordFromEntry, normalizeApiSourceEntry } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelSources';
import { parseStorageQuantityToGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import {
  buildDataVolumeFromVolumeStatus,
  buildStorageClassNameOriginalFromInfo,
  findDataVolumeInGroupedSpec,
  findDataVolumeStatusEntry,
  findModelDataVolumeInInferenceSpec,
  getVolumeMountPath,
  normalizeDataVolumeForForm,
  resolveDataVolumeStorageClassFromStatus,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import {
  PRIMARY_DATA_VOLUME_FIELD,
  extractSystemDiskSizesFromGroups,
  findGroupVolume,
  getPrimaryGroupFromValues,
  isExistingDataVolume,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { MASTER_SYSTEM_DISK_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import {
  buildSystemDiskFormFields,
  enrichEmbeddedNodesFieldJson,
  extractEmbeddedNodeConfigFromGroup,
  parseDetailGroupsToNodeFormFields,
  parseNodesField,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { resolveMaasInferenceSourceForForm } from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import type { ContainerDeployEnvVarItem } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigCore';
import { IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigFields';
import {
  normalizeInstanceSchedulingFromContainerDeployConfig,
  parseContainerDeployConfigRaw,
  stripContainerDeployConfigFromGroupSystem,
} from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import { buildImageItemFromInferenceSystem } from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import { deserializeServiceListFromApi } from '@/delivery-type-plugin/capabilities/service';
import { SERVICE_MOUNT_GROUP_NAME_FIELD, resolveServiceMountGroupNamesFromServices } from '@/delivery-type-plugin/capabilities/service';
import { buildDefaultDirectResourceModules } from '@/delivery-type-plugin/platform/scene-layout/utils/resolveModuleDefaults';

function decodeS3Credential(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  try {
    return Base64.decode(raw);
  } catch {
    return raw;
  }
}

function normalizeModelSourceS3ForForm(source?: MaasModelSource): MaasModelSource | undefined {
  if (!source?.s3?.secretRef) return source;
  return {
    ...source,
    s3: {
      ...source.s3,
      secretRef: {
        accessKeyID: decodeS3Credential(source.s3.secretRef.accessKeyID),
        secretKey: decodeS3Credential(source.s3.secretRef.secretKey),
      },
    },
  };
}

function resolveTopologyCommonEnvForForm(topology?: MaasInferenceTopologySpec): ModelInstanceFormValues['commonEnv'] {
  if (!Array.isArray(topology?.environment?.env) || !topology.environment.env.length) return undefined;
  return topology.environment.env.map((item) => ({ name: item.name, value: item.value ?? '' }));
}

/**
 * 将推理镜像预填环境变量的 _self 元数据挂回 commonEnv，
 * 使编辑页与创建页一致：框架预填项 name 锁定且不可删除。
 */
function attachImageEnvMetaToCommonEnv(
  commonEnv: NonNullable<ModelInstanceFormValues['commonEnv']>,
  imageEnvVars: ContainerDeployEnvVarItem[] | undefined,
): NonNullable<ModelInstanceFormValues['commonEnv']> {
  const metaByKey = new Map<string, ContainerDeployEnvVarItem>();
  (imageEnvVars || []).forEach((item) => {
    const key = String(item?.key ?? '').trim();
    if (key) metaByKey.set(key, item);
  });
  if (!metaByKey.size) return commonEnv;

  return commonEnv.map((item) => {
    const name = String(item?.name ?? '').trim();
    const meta = metaByKey.get(name);
    if (!meta) return item;
    return {
      ...item,
      _self: JSON.stringify(meta),
    };
  });
}

function resolveInferenceServiceForForm(spec: ModelInstanceSpec): unknown {
  const topLevel = Array.isArray(spec.network?.service) ? spec.network.service : undefined;
  if (!topLevel?.length) return undefined;
  return toFormJsonString(deserializeServiceListFromApi(topLevel));
}

function resolveRunModeFromTopology(topology?: MaasInferenceTopologySpec): MaasRunMode {
  if (topology?.headGroupSpec && (topology.workerGroupSpecs?.length ?? 0) > 0) {
    return RunModeEnum.PrimarySecondary;
  }
  return RunModeEnum.Normal;
}

/** 从 spec.inference 解析非 PD 节点（不含顶层 head/worker 回退） */
function resolveNonPdInferenceForForm(inference?: ModelInstanceSpec['inference']): {
  runMode: MaasRunMode;
  headGroupSpec?: MaasInferenceTopologySpec['headGroupSpec'];
  workerGroupSpecs?: MaasInferenceTopologySpec['workerGroupSpecs'];
} {
  if (inference?.primarySecondary) {
    return {
      runMode: resolveRunModeFromTopology(inference.primarySecondary),
      headGroupSpec: inference.primarySecondary.headGroupSpec,
      workerGroupSpecs: inference.primarySecondary.workerGroupSpecs,
    };
  }
  if (inference?.normal) {
    return {
      runMode: RunModeEnum.Normal,
      headGroupSpec: inference.normal.headGroupSpec,
      workerGroupSpecs: inference.normal.workerGroupSpecs,
    };
  }
  return { runMode: RunModeEnum.Normal };
}

function hydrateTopologyToNodeFields(topology: MaasInferenceTopologySpec | undefined, runMode: MaasRunMode) {
  if (!topology) {
    return { masterNodes: undefined as string | undefined, workerNodes: undefined as string | undefined };
  }
  const commonEnv = resolveTopologyCommonEnvForForm(topology);
  return parseDetailGroupsToNodeFormFields(
    {
      runMode,
      headGroupSpec: topology.headGroupSpec,
      workerGroupSpecs: topology.workerGroupSpecs,
      commonEnv,
    },
    { embeddedNodeConfig: true },
  );
}

function resolveServiceMountGroupNamesForForm(spec: ModelInstanceSpec): string[] | undefined {
  const topLevel = Array.isArray(spec.network?.service) ? spec.network.service : undefined;
  const groupNames = resolveServiceMountGroupNamesFromServices(topLevel);
  return groupNames.length ? groupNames : undefined;
}

/** 从 maasForm.containerDeployConfig 回填镜像选择器与资源调度 hidden 字段 */
function applyContainerDeployConfigToFormValues(formValues: ModelInstanceFormValues, rawConfig: unknown): ModelInstanceFormValues {
  if (rawConfig == null || rawConfig === '') return formValues;

  const containerDeployConfig = parseContainerDeployConfigRaw(rawConfig as string | Record<string, unknown>);
  let next = { ...formValues };

  if (!parseFormJsonField(next._imageContainerDeployInstanceScheduling)) {
    const instanceScheduling = normalizeInstanceSchedulingFromContainerDeployConfig(
      containerDeployConfig as unknown as Record<string, unknown>,
    );
    if (instanceScheduling) {
      next = { ...next, [IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD]: toFormJsonString(instanceScheduling) };
    }
  }

  const system = parseFormJsonField<Record<string, unknown>>(next._inferenceSystem);
  if (system && !parseFormJsonField(next.inferenceFrameworkImage)) {
    const imageItem = buildImageItemFromInferenceSystem({
      ...system,
      options: {
        ...((system.options as Record<string, unknown>) || {}),
        containerDeployConfig: rawConfig,
      },
    });
    if (imageItem) {
      next = { ...next, inferenceFrameworkImage: JSON.stringify(imageItem) };
    }
  }

  const healthCheck = parseHealthCheckFromDeployConfig(
    (containerDeployConfig as unknown as Record<string, unknown>)?.healthCheck as Record<string, unknown> | undefined,
  );
  if (healthCheck) {
    next = { ...next, _containerDeployHealthCheck: healthCheck as Record<string, unknown> };
  }

  return next;
}

/** API 详情 Workload → 直建/编辑 innerForm（仅从持久化 Spec 回填） */
export function hydrateModelInstanceDetailToForm(detail: MaasWorkload): ModelInstanceFormValues {
  const spec = cloneDeep(detail.spec || {}) as ModelInstanceSpec;
  const inference = spec.inference;
  const pdSeparation = !!(inference?.prefillDecode?.prefill || inference?.prefillDecode?.decode);
  let usedFlatGroupApi = false;
  let nonPd = pdSeparation ? undefined : resolveNonPdInferenceForForm(inference);
  if (!pdSeparation) {
    const hasInferenceTopology = !!(nonPd?.headGroupSpec || (nonPd?.workerGroupSpecs?.length ?? 0) > 0);
    const hasFlatTopology = !!(spec.headGroupSpec || (spec.workerGroupSpecs?.length ?? 0) > 0);
    // 训练集群等 flat API：顶层 head/worker，无 spec.inference
    if (!hasInferenceTopology && hasFlatTopology) {
      usedFlatGroupApi = true;
      nonPd = {
        runMode:
          (spec.runMode as MaasRunMode | undefined) ||
          resolveRunModeFromTopology({
            headGroupSpec: spec.headGroupSpec,
            workerGroupSpecs: spec.workerGroupSpecs,
          }),
        headGroupSpec: spec.headGroupSpec,
        workerGroupSpecs: spec.workerGroupSpecs,
      };
    }
  }
  const runMode = nonPd?.runMode ?? RunModeEnum.Normal;
  const displayHeadGroupSpec = nonPd?.headGroupSpec;
  const displayWorkerGroupSpecs = nonPd?.workerGroupSpecs;
  const framework = (getFrameworkFromWorkload(detail) as MaasFramework | undefined) || spec.framework;
  const fallbackMountPath = get(spec, 'model.mountPath') || '/models';

  const primaryGroup = getPrimaryGroupFromValues(
    {
      headGroupSpec: displayHeadGroupSpec,
      workerGroupSpecs: displayWorkerGroupSpecs,
      runMode,
    },
    runMode,
  );
  const hydratedRegistry =
    readGroupSystemRegistry(primaryGroup) ??
    readGroupSystemRegistry(displayHeadGroupSpec) ??
    readGroupSystemRegistry(displayWorkerGroupSpecs?.[0]);
  let rawDataVolume: Record<string, unknown> | undefined;
  if (usedFlatGroupApi) {
    rawDataVolume =
      findGroupVolume(get(primaryGroup, 'resource.storage.volume') as Record<string, unknown>[] | undefined, 'Data') ||
      findDataVolumeInGroupedSpec({
        headGroupSpec: displayHeadGroupSpec,
        workerGroupSpecs: displayWorkerGroupSpecs,
      });
  } else {
    rawDataVolume =
      findModelDataVolumeInInferenceSpec(spec.inference) ||
      findGroupVolume(get(primaryGroup, 'resource.storage.volume') as Record<string, unknown>[] | undefined, 'Data') ||
      findDataVolumeInGroupedSpec({
        headGroupSpec: displayHeadGroupSpec,
        workerGroupSpecs: displayWorkerGroupSpecs,
      });
  }
  if (!rawDataVolume) {
    const statusEntry = findDataVolumeStatusEntry(detail.status?.volumeStatus);
    if (statusEntry) {
      rawDataVolume = buildDataVolumeFromVolumeStatus(statusEntry, fallbackMountPath);
    }
  }
  const dataVolume = rawDataVolume ? normalizeDataVolumeForForm(cloneDeep(rawDataVolume), fallbackMountPath) : undefined;
  const modelMountPath = dataVolume ? getVolumeMountPath(dataVolume) || fallbackMountPath : fallbackMountPath;
  const systemDiskSizes = extractSystemDiskSizesFromGroups({
    headGroupSpec: displayHeadGroupSpec,
    workerGroupSpecs: displayWorkerGroupSpecs,
  });

  const hydratedModelSources = (() => {
    const rawList = Array.isArray(spec.model?.sources) ? spec.model.sources : [];
    const parsed = rawList
      .map((item) => normalizeApiSourceEntry(item as unknown as Record<string, unknown>))
      .filter((item): item is NonNullable<typeof item> => !!item);
    const withS3 = parsed.map((entry) => {
      const record = extractSourceRecordFromEntry(entry as unknown as Record<string, unknown>);
      const normalized = normalizeModelSourceS3ForForm(record as MaasModelSource);
      if (!normalized) return entry;
      return { name: entry.name, size: entry.size, ...normalized };
    });
    if (!dataVolume || !isExistingDataVolume(dataVolume)) return withS3;
    return withS3.map((entry) => {
      const record = extractSourceRecordFromEntry(entry as unknown as Record<string, unknown>);
      const cleared = clearExistingPvcModelSourceForForm(record as MaasModelSource, entry.name) || {};
      return { name: entry.name, size: entry.size, ...cleared };
    });
  })();

  const nonPdTopologyEarly = pdSeparation ? undefined : inference?.primarySecondary || inference?.normal;
  const commonEnv = usedFlatGroupApi
    ? resolveTopologyCommonEnvForForm({ environment: spec.environment })
    : resolveTopologyCommonEnvForForm(nonPdTopologyEarly);

  const containerDeployConfig = readModelInstanceContainerDeployConfigFromWorkload(detail);
  const formSnapshot = readMaasFormAnnotationSnapshot(detail);

  const nodeFields = parseDetailGroupsToNodeFormFields(
    {
      runMode,
      headGroupSpec: displayHeadGroupSpec,
      workerGroupSpecs: displayWorkerGroupSpecs,
      commonEnv,
    },
    { embeddedNodeConfig: true },
  );

  const prefillRunMode = inference?.prefillDecode?.prefill ? resolveRunModeFromTopology(inference.prefillDecode.prefill) : undefined;
  const decodeRunMode = inference?.prefillDecode?.decode ? resolveRunModeFromTopology(inference.prefillDecode.decode) : undefined;
  const prefillFields = pdSeparation
    ? hydrateTopologyToNodeFields(inference?.prefillDecode?.prefill, prefillRunMode ?? RunModeEnum.Normal)
    : undefined;
  const decodeFields = pdSeparation
    ? hydrateTopologyToNodeFields(inference?.prefillDecode?.decode, decodeRunMode ?? RunModeEnum.Normal)
    : undefined;

  const enrichInferenceOpts = {
    annotationInferenceFrameworkImage: formSnapshot.inferenceFrameworkImage,
    containerDeployConfig,
  };
  const masterNodes = enrichEmbeddedNodesFieldJson(nodeFields.masterNodes, enrichInferenceOpts);
  const workerNodes = enrichEmbeddedNodesFieldJson(nodeFields.workerNodes, enrichInferenceOpts);
  const gatewayEmbedded = spec.gateway ? extractEmbeddedNodeConfigFromGroup(spec.gateway) : undefined;
  const gatewayNodes = gatewayEmbedded ? enrichEmbeddedNodesFieldJson(JSON.stringify([gatewayEmbedded]), enrichInferenceOpts) : undefined;

  const legacyInferenceSystem = nodeFields._inferenceSystem
    ? stripContainerDeployConfigFromGroupSystem(nodeFields._inferenceSystem as Record<string, unknown>)
    : undefined;
  /** 训练集群 flatGroups 仍回填顶层 initCommand；模型实例拓扑级命令不再回填 */
  const nonPdInitCommand = usedFlatGroupApi ? spec.initCommand : undefined;
  const kvOffload = usedFlatGroupApi ? undefined : spec.kvOffload;
  const kvOffloadRunMode = kvOffload?.headGroupSpec ? RunModeEnum.PrimarySecondary : RunModeEnum.Normal;
  const kvOffloadFields = kvOffload ? hydrateTopologyToNodeFields(kvOffload, kvOffloadRunMode) : undefined;
  const kvOffloadCommonEnv = resolveTopologyCommonEnvForForm(kvOffload);

  const {
    headGroupSpec: _omitHead,
    workerGroupSpecs: _omitWorkers,
    replicas: _omitReplicas,
    runMode: _omitRunMode,
    inference: _omitInference,
    taskTemplate: _omitTaskTemplate,
    kvOffload: _omitKvOffload,
    ...specScalars
  } = spec;

  const nonPdTopology = nonPdTopologyEarly;
  const prefillTopology = inference?.prefillDecode?.prefill;
  const decodeTopology = inference?.prefillDecode?.decode;
  const prefillCommonEnv = resolveTopologyCommonEnvForForm(prefillTopology);
  const decodeCommonEnv = resolveTopologyCommonEnvForForm(decodeTopology);
  const flatTaskTemplate = usedFlatGroupApi && Array.isArray(spec.taskTemplate) ? spec.taskTemplate : undefined;

  let formValues: ModelInstanceFormValues = {
    ...specScalars,
    namespace: detail.metadata?.namespace,
    workspace: spec.workspace || undefined,
    framework,
    runMode,
    runtime: normalizeMaasRuntime(spec.runtime) || spec.runtime,
    network: spec.network || { vpc: undefined },
    // env 已映射到 commonEnv；清空双源，避免确认/提交时空 commonEnv 回退到 environment.env
    environment: undefined,
    model: {
      mountPath: modelMountPath || spec.model?.mountPath || '/models',
      sources: hydratedModelSources,
      ...(spec.model?.types?.length ? { types: spec.model.types } : {}),
    },
    _modelStorageMode: dataVolume && isExistingDataVolume(dataVolume) ? 'existing' : 'new',
    masterNodes,
    workerNodes,
    replicas: nonPdTopology?.replicas ?? (usedFlatGroupApi ? spec.replicas : undefined),
    ...(nonPdTopology?.taskTemplate?.length
      ? { taskTemplate: toFormJsonString(nonPdTopology.taskTemplate) }
      : flatTaskTemplate?.length
        ? { taskTemplate: toFormJsonString(flatTaskTemplate) }
        : {}),
    ...(nonPdInitCommand ? { initCommand: initCommandApiToFormFields(nonPdInitCommand) as ModelInstanceFormValues['initCommand'] } : {}),
    _inferencePdSeparation: pdSeparation,
    ...(pdSeparation && prefillFields
      ? {
          prefillReplicas: prefillTopology?.replicas,
          ...(prefillTopology?.taskTemplate?.length ? { prefillTaskTemplate: toFormJsonString(prefillTopology.taskTemplate) } : {}),
          ...(prefillCommonEnv ? { prefillCommonEnv } : {}),
          prefillRunMode,
          prefillMasterNodes: prefillFields.masterNodes,
          prefillWorkerNodes: prefillFields.workerNodes,
        }
      : {}),
    ...(pdSeparation && decodeFields
      ? {
          decodeReplicas: decodeTopology?.replicas,
          ...(decodeTopology?.taskTemplate?.length ? { decodeTaskTemplate: toFormJsonString(decodeTopology.taskTemplate) } : {}),
          ...(decodeCommonEnv ? { decodeCommonEnv } : {}),
          decodeRunMode,
          decodeMasterNodes: decodeFields.masterNodes,
          decodeWorkerNodes: decodeFields.workerNodes,
        }
      : {}),
    ...(gatewayNodes ? { gatewayNodes } : {}),
    ...(kvOffloadFields
      ? {
          kvOffloadReplicas: kvOffload?.replicas,
          ...(kvOffload?.taskTemplate?.length ? { kvOffloadTaskTemplate: toFormJsonString(kvOffload.taskTemplate) } : {}),
          ...(kvOffloadCommonEnv ? { kvOffloadCommonEnv } : {}),
          kvOffloadRunMode,
          kvOffloadMasterNodes: kvOffloadFields.masterNodes,
          kvOffloadWorkerNodes: kvOffloadFields.workerNodes,
        }
      : {}),
    _inferenceSystem: legacyInferenceSystem,
    ...buildSystemDiskFormFields(systemDiskSizes),
    ...(hydratedRegistry ? { registry: hydratedRegistry } : {}),
    ...(commonEnv ? { commonEnv } : {}),
    skuResourceModules: {
      ...buildDefaultDirectResourceModules(usedFlatGroupApi ? 'TrainingCluster' : 'ModelInstance'),
      gatewayConfig: !!spec.gateway,
      ...(kvOffload ? { kvOffload: true } : {}),
    },
  };

  formValues = applyContainerDeployConfigToFormValues(formValues, containerDeployConfig);

  if (!formValues._containerDeployHealthCheck) {
    const inferenceSystem = parseFormJsonField<Record<string, unknown>>(formValues._inferenceSystem);
    const livenessProbe = inferenceSystem?.livenessProbe as Record<string, unknown> | undefined;
    const fromProbe = healthCheckFromLivenessProbe(livenessProbe);
    if (fromProbe) {
      formValues = { ...formValues, _containerDeployHealthCheck: fromProbe as Record<string, unknown> };
    }
  }

  const healthCheckForm = formValues._containerDeployHealthCheck as Parameters<typeof parseHealthCheckFromDeployConfig>[0];
  const parsedHealthCheck = healthCheckForm ? parseHealthCheckFromDeployConfig(healthCheckForm) : undefined;
  if (parsedHealthCheck) {
    formValues = {
      ...formValues,
      [MAAS_INFERENCE_SOURCE_FIELD]: resolveMaasInferenceSourceForForm(formValues),
      skuResourceModules: {
        ...(formValues.skuResourceModules as Record<string, boolean>),
        healthCheck: parsedHealthCheck.enabled === true,
      },
    };
  } else {
    formValues = {
      ...formValues,
      [MAAS_INFERENCE_SOURCE_FIELD]: resolveMaasInferenceSourceForForm(formValues),
    };
  }

  if (formValues.commonEnv?.length && containerDeployConfig != null && containerDeployConfig !== '') {
    const imageEnvVars = parseContainerDeployConfigRaw(containerDeployConfig as string | Record<string, unknown>).envVars;
    formValues = {
      ...formValues,
      commonEnv: attachImageEnvMetaToCommonEnv(formValues.commonEnv, imageEnvVars),
    };
  }

  const storageClassInfo = resolveDataVolumeStorageClassFromStatus(detail);
  if (formSnapshot.masterSystemDiskPricing != null && typeof formSnapshot.masterSystemDiskPricing === 'object') {
    formValues = {
      ...formValues,
      masterSystemDiskPricing: formSnapshot.masterSystemDiskPricing as ModelInstanceFormValues['masterSystemDiskPricing'],
    };
  }
  if (formSnapshot.workerSystemDiskPricing != null && typeof formSnapshot.workerSystemDiskPricing === 'object') {
    formValues = {
      ...formValues,
      workerSystemDiskPricing: formSnapshot.workerSystemDiskPricing as ModelInstanceFormValues['workerSystemDiskPricing'],
    };
  }

  if (dataVolume) {
    const hydratedDataVolume = {
      ...cloneDeep(dataVolume),
      ...(storageClassInfo ? { storageClassNameOriginal: buildStorageClassNameOriginalFromInfo(storageClassInfo) } : {}),
    };
    formValues = {
      ...formValues,
      [PRIMARY_DATA_VOLUME_FIELD]: JSON.stringify(hydratedDataVolume),
      maasStorageConfigured: '1',
    };
  } else if (formSnapshot.primaryDataVolume != null && formSnapshot.primaryDataVolume !== '') {
    // 无节点组时 Data 卷仅存在于 maasForm（SKU 自定义规格仅配模型与存储）
    const raw = formSnapshot.primaryDataVolume;
    formValues = {
      ...formValues,
      [PRIMARY_DATA_VOLUME_FIELD]: typeof raw === 'string' ? raw : JSON.stringify(raw),
      maasStorageConfigured: '1',
    };
  }

  const modelType = getModelCategoryFromWorkload(detail);
  if (hasModelCategory(modelType)) {
    formValues = { ...formValues, [MODEL_CATEGORY_FORM_FIELD]: modelType };
  }

  const primaryWorker = parseNodesField(formValues.workerNodes)[0] as { inferenceFrameworkImage?: string } | undefined;
  if (primaryWorker?.inferenceFrameworkImage && !parseFormJsonField(formValues.inferenceFrameworkImage)) {
    formValues = { ...formValues, inferenceFrameworkImage: primaryWorker.inferenceFrameworkImage };
  }

  const imageItem = parseFormJsonField<CommonImageItemType>(formValues.inferenceFrameworkImage);
  if (imageItem) {
    formValues = {
      ...formValues,
      [IMAGE_STORAGE_VOLUMES_FIELD]: serializeImageStorageVolumesField(rebuildImageStorageVolumes(imageItem)),
    };
  }

  const inferenceService = resolveInferenceServiceForForm(spec);
  if (inferenceService) {
    formValues = { ...formValues, _inferenceService: inferenceService };
  }
  const serviceMountGroupNames = resolveServiceMountGroupNamesForForm(spec);
  if (serviceMountGroupNames?.length) {
    formValues = { ...formValues, [SERVICE_MOUNT_GROUP_NAME_FIELD]: serviceMountGroupNames };
  }

  return formValues;
}

export { getModelSizeGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
